import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Grid,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
} from 'antd';
import {
    AppstoreOutlined,
    DatabaseOutlined,
    PartitionOutlined,
    ReloadOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { loadDashboardMetrics, type DashboardMetrics } from '../../dashboard/dashboardMetrics';
import { FIRESTORE_DB_QUESTION_BANK_ID, getFirebaseWebConfig } from '../../firebase/config';
import './HomePage.css';

const { Title, Paragraph, Text } = Typography;

function infrastructureRows() {
    const cfg = getFirebaseWebConfig();
    const envLabel =
        process.env.REACT_APP_ADMIN_ENV?.trim() ||
        (process.env.NODE_ENV === 'production' ? 'Production' : 'Development');

    return [
        { key: 'env', label: 'Build', children: <Tag color="blue">{envLabel}</Tag> },
        { key: 'proj', label: 'Firebase project', children: <Text code>{cfg.projectId}</Text> },
        {
            key: 'db',
            label: 'Firestore QA DB',
            children: <Text code>{FIRESTORE_DB_QUESTION_BANK_ID}</Text>,
        },
    ];
}

function HomePage() {
    const screens = Grid.useBreakpoint();
    const isNarrow = !screens.md;
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const m = await loadDashboardMetrics();
            setMetrics(m);
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Could not load dashboard metrics.';
            setError(msg);
            setMetrics(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const loadedLabel = metrics
        ? new Date(metrics.loadedAt).toLocaleString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              day: 'numeric',
              month: 'short',
          })
        : null;

    return (
        <div className="home-page vw-section-stack">
            <header className="home-page__header">
                <Title level={3} className="home-page__title">
                    Home
                </Title>
                <Paragraph type="secondary" className="home-page__lead">
                    Overview of Test Catalog and question bank sizes (Firestore aggregates). Shortcuts below jump
                    to main tools.
                </Paragraph>
            </header>

            {error ? (
                <Alert
                    type="warning"
                    showIcon
                    message="Metrics unavailable"
                    description={error}
                    action={
                        <Button size="small" type="primary" onClick={refresh}>
                            Retry
                        </Button>
                    }
                    className="home-page__alert"
                />
            ) : null}

            <Card
                title="Snapshot"
                extra={
                    <Space wrap align="center">
                        {loadedLabel ? (
                            <Text type="secondary" className="home-page__updated">
                                Updated {loadedLabel}
                            </Text>
                        ) : null}
                        <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading} size="small">
                            Refresh
                        </Button>
                    </Space>
                }
                className="home-page__card home-page__metrics-card"
            >
                <Spin spinning={loading}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8}>
                            <Statistic title="Test groups" value={metrics?.testGroupCount ?? '—'} />
                        </Col>
                        <Col xs={24} sm={8}>
                            <Statistic title="Tests (catalog)" value={metrics?.testCatalogTestCount ?? '—'} />
                        </Col>
                        <Col xs={24} sm={8}>
                            <Statistic
                                title="Questions (sum of banks)"
                                value={metrics?.questionTotalEstimate ?? '—'}
                                suffix={
                                    metrics?.questionBankCounts?.some((r) => r.count < 0)
                                        ? ' (partial)'
                                        : undefined
                                }
                            />
                        </Col>
                    </Row>

                    <Title level={5} className="home-page__subsection-title">
                        Question banks by collection
                    </Title>
                    <Paragraph type="secondary" className="home-page__hint">
                        Configure <Text code>REACT_APP_DASHBOARD_QUESTION_COLLECTIONS</Text> (comma-separated)
                        to match the states you track. Failed counts show as — (rules or missing collection).
                    </Paragraph>
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="collectionId"
                        dataSource={metrics?.questionBankCounts ?? []}
                        columns={[
                            {
                                title: 'Collection',
                                dataIndex: 'collectionId',
                                key: 'c',
                                render: (v: string) => <Text code>{v}</Text>,
                            },
                            {
                                title: 'Documents',
                                dataIndex: 'count',
                                key: 'n',
                                width: 120,
                                align: 'right',
                                render: (n: number) =>
                                    n < 0 ? (
                                        <Text type="secondary">—</Text>
                                    ) : (
                                        <Text strong>{n.toLocaleString()}</Text>
                                    ),
                            },
                        ]}
                        locale={{ emptyText: loading ? 'Loading…' : 'No collections configured' }}
                    />
                </Spin>
            </Card>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card title="Shortcuts" className="home-page__card">
                        <Space direction="vertical" size="middle" className="home-page__shortcuts">
                            <Link to="/test-catalog" className="home-page__shortcut-link">
                                <AppstoreOutlined className="home-page__shortcut-icon" aria-hidden />
                                <span>
                                    <Text strong>Test catalog</Text>
                                    <Text type="secondary"> — Groups, tests, question ID links</Text>
                                </span>
                            </Link>
                            <Link to="/test-tree" className="home-page__shortcut-link">
                                <PartitionOutlined className="home-page__shortcut-icon" aria-hidden />
                                <span>
                                    <Text strong>Test tree</Text>
                                    <Text type="secondary"> — Read-only tree + search</Text>
                                </span>
                            </Link>
                            <Link to="/firebase-storage" className="home-page__shortcut-link">
                                <DatabaseOutlined className="home-page__shortcut-icon" aria-hidden />
                                <span>
                                    <Text strong>Firebase storage</Text>
                                    <Text type="secondary"> — Question bank, Firestore tools, assets</Text>
                                </span>
                            </Link>
                            <Link to="/postgres-storage" className="home-page__shortcut-link">
                                <DatabaseOutlined className="home-page__shortcut-icon" aria-hidden />
                                <span>
                                    <Text strong>Postgres storage</Text>
                                    <Text type="secondary"> — Settings, video manuals, migration, data management</Text>
                                </span>
                            </Link>
                            <Link to="/ebook" className="home-page__shortcut-link">
                                <AppstoreOutlined className="home-page__shortcut-icon" aria-hidden />
                                <span>
                                    <Text strong>Ebook</Text>
                                    <Text type="secondary"> — Process Raw DMV pages (no image storage)</Text>
                                </span>
                            </Link>
                            <Link to="/project-tests" className="home-page__shortcut-link">
                                <DatabaseOutlined className="home-page__shortcut-icon" aria-hidden />
                                <span>
                                    <Text strong>Project tests</Text>
                                    <Text type="secondary"> — Postgres catalog audits (read-only)</Text>
                                </span>
                            </Link>
                            <Link to="/account" className="home-page__shortcut-link">
                                <UserOutlined className="home-page__shortcut-icon" aria-hidden />
                                <span>
                                    <Text strong>Account</Text>
                                    <Text type="secondary"> — Profile, session, preferences</Text>
                                </span>
                            </Link>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card title="Infrastructure" className="home-page__card">
                        <Descriptions
                            size="small"
                            column={isNarrow ? 1 : 1}
                            items={infrastructureRows()}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default HomePage;
